import { CreditCard, Palette, PaperPlaneTilt, Receipt } from "@phosphor-icons/react";
import { GROW_METHOD_OPTIONS, GROW_VAT_OPTIONS } from "./growUi";

/**
 * GrowSettingsForm — the non-secret Grow settings (shared by the setup wizard
 * and the post-connect settings modal). Controlled: `value` is the state built
 * by `buildGrowSettingsState`, `onChange(next)` receives the whole object.
 *
 * Sections: payment methods · VAT type · sending · receipts · branding.
 */
export default function GrowSettingsForm({ value, onChange, disabled = false }) {
  const set = (patch) => onChange({ ...value, ...patch });
  const setBranding = (patch) => onChange({ ...value, branding: { ...value.branding, ...patch } });

  const toggleMethod = (id) => {
    const has = value.methods.includes(id);
    if (has && value.methods.length === 1) return; // keep at least one method
    set({ methods: has ? value.methods.filter((m) => m !== id) : [...value.methods, id] });
  };

  return (
    <div className="grow-form">
      {/* Methods */}
      <section className="grow-form-section">
        <header className="grow-form-section-head">
          <span className="grow-form-section-icon"><CreditCard size={16} weight="fill" /></span>
          <div>
            <h4>אמצעי תשלום</h4>
            <p>מה המטופל יוכל לבחור בעמוד התשלום. חייב להישאר לפחות אמצעי אחד.</p>
          </div>
        </header>
        <div className="grow-method-grid">
          {GROW_METHOD_OPTIONS.map((m) => {
            const on = value.methods.includes(m.id);
            return (
              <button
                key={m.id}
                type="button"
                className={`grow-method-chip ${on ? "on" : ""}`}
                onClick={() => toggleMethod(m.id)}
                disabled={disabled}
                aria-pressed={on}
              >
                <span className="grow-method-label">{m.label}</span>
                <span className="grow-method-hint">{m.hint}</span>
              </button>
            );
          })}
        </div>
      </section>

      {/* VAT */}
      <section className="grow-form-section">
        <header className="grow-form-section-head">
          <span className="grow-form-section-icon"><Receipt size={16} weight="fill" /></span>
          <div>
            <h4>סוג עוסק</h4>
            <p>חייב להתאים להגדרת העסק ב-Grow, אחרת הקישור ייכשל.</p>
          </div>
        </header>
        <div className="grow-radio-row">
          {GROW_VAT_OPTIONS.map((o) => (
            <label key={o.value} className={`grow-radio ${value.vatType === o.value ? "on" : ""}`}>
              <input
                type="radio"
                name="grow-vat"
                checked={value.vatType === o.value}
                onChange={() => set({ vatType: o.value })}
                disabled={disabled}
              />
              <span className="grow-radio-label">{o.label}</span>
              <span className="grow-radio-hint">{o.hint}</span>
            </label>
          ))}
        </div>
      </section>

      {/* Sending */}
      <section className="grow-form-section">
        <header className="grow-form-section-head">
          <span className="grow-form-section-icon"><PaperPlaneTilt size={16} weight="fill" /></span>
          <div>
            <h4>שליחה ומעקב</h4>
            <p>דרישת תשלום נשלחת למטופל בוואטסאפ (או SMS) מתוך CliniKit.</p>
          </div>
        </header>

        <div className="grow-toggle-row">
          <div className="grow-toggle-text">
            <span className="grow-toggle-label">שליחה למטופל מיד עם יצירת דרישת תשלום</span>
            <span className="grow-toggle-hint">כבוי = הקישור נוצר ומועתק בלבד, והשליחה נעשית מתוך פרטי הדרישה.</span>
          </div>
          <label className="grow-toggle">
            <input
              type="checkbox"
              checked={value.sendImmediately}
              onChange={(e) => set({ sendImmediately: e.target.checked })}
              disabled={disabled}
            />
            <span className="grow-toggle-slider" />
          </label>
        </div>

        <div className="grow-toggle-row">
          <div className="grow-toggle-text">
            <span className="grow-toggle-label">שליחה אוטומטית בסיום פגישה</span>
            <span className="grow-toggle-hint">כשפגישה מסתיימת ולא נרשם תשלום — נשלחת דרישת תשלום לבד.</span>
          </div>
          <label className="grow-toggle">
            <input
              type="checkbox"
              checked={value.autoSendOnCompletion}
              onChange={(e) => set({ autoSendOnCompletion: e.target.checked })}
              disabled={disabled}
            />
            <span className="grow-toggle-slider" />
          </label>
        </div>

        <div className="grow-field-grid">
          <label className="grow-field">
            <span>עיכוב לפני שליחה אוטומטית (דקות)</span>
            <input
              type="number"
              min={0}
              max={1440}
              value={value.autoSendDelayMinutes}
              onChange={(e) => set({ autoSendDelayMinutes: Math.max(0, Number(e.target.value) || 0) })}
              disabled={disabled || !value.autoSendOnCompletion}
            />
          </label>
          <label className="grow-field">
            <span>תזכורת לצוות אם לא שולם (ימים)</span>
            <input
              type="number"
              min={0}
              max={60}
              value={value.reminderAfterDays}
              onChange={(e) => set({ reminderAfterDays: Math.max(0, Number(e.target.value) || 0) })}
              disabled={disabled}
            />
            <small>0 = ללא תזכורת</small>
          </label>
          <label className="grow-field">
            <span>תוקף קישור (ימים)</span>
            <input
              type="number"
              min={1}
              max={365}
              value={value.linkExpiryDays}
              onChange={(e) => set({ linkExpiryDays: Math.max(1, Number(e.target.value) || 1) })}
              disabled={disabled}
            />
          </label>
        </div>

        <div className="grow-toggle-row">
          <div className="grow-toggle-text">
            <span className="grow-toggle-label">תזכורת אוטומטית למטופל</span>
            <span className="grow-toggle-hint">בכל מחזור תזכורת נשלחת למטופל גם הודעת תזכורת עם הקישור (וואטסאפ/SMS).</span>
          </div>
          <label className="grow-toggle">
            <input
              type="checkbox"
              checked={value.autoRemindPatient}
              onChange={(e) => set({ autoRemindPatient: e.target.checked })}
              disabled={disabled || !value.reminderAfterDays}
            />
            <span className="grow-toggle-slider" />
          </label>
        </div>

        <div className="grow-toggle-row">
          <div className="grow-toggle-text">
            <span className="grow-toggle-label">שליחת הקבלה למטופל</span>
            <span className="grow-toggle-hint">כש-Grow מפיקה קבלה, היא נשלחת למטופל בוואטסאפ/SMS.</span>
          </div>
          <label className="grow-toggle">
            <input
              type="checkbox"
              checked={value.sendReceiptToPatient}
              onChange={(e) => set({ sendReceiptToPatient: e.target.checked })}
              disabled={disabled || value.invoiceMode !== "grow"}
            />
            <span className="grow-toggle-slider" />
          </label>
        </div>

        <div className="grow-toggle-row">
          <div className="grow-toggle-text">
            <span className="grow-toggle-label">קבלה אוטומטית מ-Grow</span>
            <span className="grow-toggle-hint">כשהתשלום מתקבל, Grow מפיקה קבלה והיא נשמרת על הפגישה.</span>
          </div>
          <label className="grow-toggle">
            <input
              type="checkbox"
              checked={value.invoiceMode === "grow"}
              onChange={(e) => set({ invoiceMode: e.target.checked ? "grow" : "none" })}
              disabled={disabled}
            />
            <span className="grow-toggle-slider" />
          </label>
        </div>
      </section>

      {/* Branding */}
      <section className="grow-form-section">
        <header className="grow-form-section-head">
          <span className="grow-form-section-icon"><Palette size={16} weight="fill" /></span>
          <div>
            <h4>מיתוג עמוד התשלום</h4>
            <p>אופציונלי. הלוגו נלקח מהגדרות הקליניקה.</p>
          </div>
        </header>
        <div className="grow-field-grid">
          <label className="grow-field">
            <span>צבע רקע</span>
            <div className="grow-color-input">
              <input
                type="color"
                value={value.branding.backgroundColor || "#ffffff"}
                onChange={(e) => setBranding({ backgroundColor: e.target.value })}
                disabled={disabled}
                aria-label="צבע רקע"
              />
              <button
                type="button"
                className="grow-link-btn"
                onClick={() => setBranding({ backgroundColor: "" })}
                disabled={disabled || !value.branding.backgroundColor}
              >
                נקה
              </button>
            </div>
          </label>
          <label className="grow-field">
            <span>צבע כפתור</span>
            <div className="grow-color-input">
              <input
                type="color"
                value={value.branding.buttonColor || "#4dd9b4"}
                onChange={(e) => setBranding({ buttonColor: e.target.value })}
                disabled={disabled}
                aria-label="צבע כפתור"
              />
              <button
                type="button"
                className="grow-link-btn"
                onClick={() => setBranding({ buttonColor: "" })}
                disabled={disabled || !value.branding.buttonColor}
              >
                נקה
              </button>
            </div>
          </label>
          <label className="grow-field">
            <span>טקסט כפתור התשלום</span>
            <input
              type="text"
              maxLength={20}
              placeholder="לתשלום"
              value={value.branding.paymentButtonText}
              onChange={(e) => setBranding({ paymentButtonText: e.target.value })}
              disabled={disabled}
            />
            <small>עד 20 תווים, ללא תווים מיוחדים</small>
          </label>
        </div>
      </section>
    </div>
  );
}
